import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {{ target: HTMLElement, identifier: number, x: number, y: number, time: number } | null} */
  #revealTap = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.addEventListener('touchmove', this.#onTouchMove, { passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: false });
    this.addEventListener('touchcancel', this.#cancelRevealTap);
    this.addEventListener('contextmenu', this.#cancelRevealTap);
    this.addEventListener('scroll', this.#cancelRevealTap, true);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('touchstart', this.#onTouchStart);
    this.removeEventListener('touchmove', this.#onTouchMove);
    this.removeEventListener('touchend', this.#onTouchEnd);
    this.removeEventListener('touchcancel', this.#cancelRevealTap);
    this.removeEventListener('contextmenu', this.#cancelRevealTap);
    this.removeEventListener('scroll', this.#cancelRevealTap, true);
    this.#cancelRevealTap();
  }

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    this.#cancelRevealTap();
    if (event.defaultPrevented || event.touches.length !== 1 || !(event.target instanceof Element)) return;

    const target = event.target.closest('a[href], summary');
    if (!(target instanceof HTMLElement) || !this.refs.menuDrawer.contains(target)) return;

    const isRevealing = this.refs.menuDrawer
      .getAnimations({ subtree: true })
      .some(
        (animation) =>
          animation instanceof CSSAnimation &&
          animation.animationName === 'menu-drawer-nav-open' &&
          animation.playState === 'running'
      );
    if (!isRevealing) return;

    const touch = event.touches[0];
    if (!touch) return;
    this.#revealTap = {
      target,
      identifier: touch.identifier,
      x: touch.clientX,
      y: touch.clientY,
      time: event.timeStamp,
    };
  };

  /** @param {TouchEvent} event */
  #onTouchMove = (event) => {
    const tap = this.#revealTap;
    if (!tap) return;
    const touch = event.touches[0];
    if (
      event.touches.length !== 1 ||
      !touch ||
      touch.identifier !== tap.identifier ||
      Math.hypot(touch.clientX - tap.x, touch.clientY - tap.y) > 10
    ) {
      this.#cancelRevealTap();
    }
  };

  /** @param {TouchEvent} event */
  #onTouchEnd = (event) => {
    const tap = this.#revealTap;
    this.#cancelRevealTap();
    if (
      !tap ||
      event.defaultPrevented ||
      !event.cancelable ||
      event.touches.length ||
      event.changedTouches.length !== 1 ||
      event.timeStamp - tap.time > 500
    ) return;

    const touch = event.changedTouches[0];
    if (
      !touch ||
      touch.identifier !== tap.identifier ||
      Math.hypot(touch.clientX - tap.x, touch.clientY - tap.y) > 10 ||
      !this.refs.menuDrawer.contains(tap.target) ||
      !tap.target.contains(this.ownerDocument.elementFromPoint(touch.clientX, touch.clientY))
    ) return;

    // Safari can suppress its synthetic click while other menu rows become visible.
    // Activate a completed tap once, without interrupting the reveal or scroll gestures.
    event.preventDefault();
    tap.target.click();
  };

  #cancelRevealTap = () => {
    this.#revealTap = null;
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
